import React, { createContext, useContext, useEffect, useState } from "react";
import { message } from "antd";
import { onSnapshot, doc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../firebaseConfig";
import { signOutEverywhere } from "../utils/authProviders";
import { inicializarPushNotifications, desregistrarPushNotifications } from "../utils/pushNotifications";
import { normalizarCoordenadas } from "../utils/geo/distancia";

// Lee la ubicación guardada; si está corrupta o es inválida se descarta en lugar de romper la app
const leerUbicacionGuardada = () => {
  try {
    const c = normalizarCoordenadas(JSON.parse(localStorage.getItem("userLocation")));
    return c ? { latitude: c.lat, longitude: c.lng } : null;
  } catch {
    return null;
  }
};

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [userData, setUserDataState] = useState();
  const [userFavorites, setUserFavorites] = useState([]);
  const [cartItems, setCartItems] = useState({ productos: [], servicios: [] });
  const [userLocation, setUserLocation] = useState(null); // Nuevo estado para la ubicación del usuario
  const [userDataAvailable, setUserDataAvailable] = useState(null); // Nueva variable de estado

  useEffect(() => {
    let unsubscribeFirestore = () => {};

    // 1. Rehidratar inmediatamente de localStorage si existe
    const storedUserData = localStorage.getItem("userData");
    let initialUserData = null;
    if (storedUserData) {
      try {
        initialUserData = JSON.parse(storedUserData);
        setUserDataState(initialUserData);
        setUserDataAvailable(true);
      } catch (e) {
        console.error("Error parseando userData de localStorage", e);
      }
    }

    // 2. Conectar con Firebase Auth para mantener la sesión real sincronizada
    const unsubscribeAuth = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        const userRef = doc(db, "roles", firebaseUser.uid);
        unsubscribeFirestore = onSnapshot(
          userRef,
          (snapshot) => {
            if (snapshot.exists()) {
              const freshData = { ...snapshot.data(), uid: firebaseUser.uid };
              localStorage.setItem("userData", JSON.stringify(freshData));
              setUserDataState(freshData);
              setUserDataAvailable(true);

              // Solo desloguear si activado es false y NO es un vendedor
              if (freshData.activado === false && freshData.role !== 1) {
                // Sin este aviso la persona solo vería que la sesión se cierra sin saber por qué
                message.error({
                  key: "cuenta-bloqueada",
                  duration: 10,
                  content: freshData.motivoBloqueo
                    ? `Tu cuenta fue suspendida. Motivo: ${freshData.motivoBloqueo}. Escríbenos a ayuda@blizu.com.mx si tienes dudas.`
                    : "Tu cuenta fue suspendida. Escríbenos a ayuda@blizu.com.mx si tienes dudas.",
                });
                clearUser();
              }
            } else {
              clearUser();
            }
          },
          (error) => {
            console.error("Error en listener de usuario:", error);
            if (!initialUserData) setUserDataAvailable(false);
          }
        );
      } else {
        // No hay sesión activa en Firebase Auth
        if (!localStorage.getItem("userData")) {
          setUserDataState(null);
          setUserDataAvailable(false);
        }
      }
    });

    return () => {
      unsubscribeAuth();
      unsubscribeFirestore();
    };
  }, []);

  useEffect(() => {
    // Cuando userData esté disponible y no sea null, establecer userDataAvailable en true
    if (userData !== undefined) {
      if(userData !== null){
        setUserDataAvailable(true);
      }else{
        setUserDataAvailable(false);
      }
   
    }
  }, [userData]);


  useEffect(() => {
    const unsubscribeUserFavorites = listenToUserFavorites(userData?.uid, setUserFavorites);
    return () => {
      unsubscribeUserFavorites();
    };
  }, [userData?.uid]);

  useEffect(() => {
    const unsubscribeCart = listenToUserCart(userData?.uid, setCartItems);
    return () => {
      unsubscribeCart();
    };
  }, [userData?.uid]);

  useEffect(() => {
    setUserLocation(leerUbicacionGuardada());

    // Mantiene sincronizadas varias pestañas abiertas
    const onStorage = (e) => {
      if (e.key === "userLocation" || e.key === null) setUserLocation(leerUbicacionGuardada());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);


  const updateUserData = (updates) => {
    setUserDataState((prev) => {
      const updated = { ...prev, ...updates };
      localStorage.setItem("userData", JSON.stringify(updated));
      return updated;
    });
  };

  const setUser = (newUserData) => {
    localStorage.setItem("userData", JSON.stringify(newUserData));
    setUserDataState(newUserData);
  };

  useEffect(() => {
    if (userData?.uid) {
      inicializarPushNotifications(userData.uid);
    }
  }, [userData?.uid]);

  const clearUser = () => {
    if (userData?.uid) {
      desregistrarPushNotifications(userData.uid);
    }
    localStorage.removeItem("userData");
    setUserDataState(null);
    signOutEverywhere();
  };

  const updateUserFavoritesLocal = (newFavorites) => {
    setUserFavorites(newFavorites);
  };

  const setUserLocationCurrent = (newLocation) => {
    const c = normalizarCoordenadas(newLocation);
    if (!c) return; // nunca se guarda una ubicación inválida
    const normalizada = { latitude: c.lat, longitude: c.lng };
    try {
      localStorage.setItem("userLocation", JSON.stringify(normalizada));
    } catch (e) {
      console.error("No se pudo guardar la ubicación", e);
    }
    setUserLocation(normalizada);
  };

  const listenToUserData = (uid, setUserData) => {
    if (!uid) return () => {};

    const userRef = doc(db, "roles", uid);
    return onSnapshot(userRef, (snapshot) => {
      if (snapshot.exists()) {
        const userData = snapshot.data();
        setUserData(userData);
        // Solo desloguear si activado es false y NO es un vendedor (el vendedor puede comprar mientras lo validan)
        if (userData.activado === false && userData.role !== 1) {
          clearUser();
        }
      } else {
        clearUser();
      }
    });
  };

  const listenToUserFavorites = (uid, setUserFavorites) => {
    if (!uid) return () => {};

    const userFavoritesRef = doc(db, "favoritos", uid);
    return onSnapshot(userFavoritesRef, (snapshot) => {
      if (snapshot.exists()) {
        const userFavoritesData = snapshot.data();
        setUserFavorites(userFavoritesData);
      } else {
        setUserFavorites([]);
      }
    });
  };

  const listenToUserCart = (uid, setCartItems) => {
    if (!uid) return () => {};

    const userCartRef = doc(db, "carritos", uid);
    return onSnapshot(userCartRef, (snapshot) => {
      if (snapshot.exists()) {
        setCartItems(snapshot.data()); // Suponiendo que 'servicios' es la propiedad que contiene los elementos del carrito
      } else {
        setCartItems({ productos: [], servicios: [] });
      }
    });
  };

  return (
    <UserContext.Provider
      value={{
        userData,
        updateUserData,
        userFavorites,
        userLocation,
        userDataAvailable,
        setUser,
        clearUser,
        logout: clearUser,
        updateUserFavoritesLocal,
        setUserLocationCurrent,
        cartItems,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  return useContext(UserContext);
};
